import { isBlankProseContent } from '@/blocks/application/helpers/isBlankProseContent';
import { openDraftIdOf } from '@/blocks/application/hooks/blockDraftRequests';
import { useCreateBlock, type CreateBlockPayload } from '@/blocks/application/hooks/useCreateBlock';
import { usePublishBlockVersion } from '@/blocks/application/hooks/usePublishBlockVersion';
import { defaultContentForKind } from '@/blocks/domain/helpers/defaultContentForKind';
import { materializeEditorSections } from '@/blocks/domain/helpers/materializeEditorSections';
import { DEFAULT_BLOCK_STYLE } from '@/blocks/domain/types';
import { HttpError } from '@/common/infrastructure/http/problemDetails';
import { defaultLogger } from '@/common/infrastructure/services/LoggerService';

/**
 * Why a new block stayed a Draft: `no-content` when the API refused the publish with a 400 (the saved
 * draft has no renderable snapshot yet, e.g. an empty prose body), `failed` for anything else.
 */
export type UnpublishedReason = 'no-content' | 'failed';

export type CreatedBlock = { blockId: string; published: true } | { blockId: string; published: false; reason: UnpublishedReason };

/**
 * New Block's "Publish": `POST /blocks` (which opens an empty Draft v1), then the same save-and-promote
 * the editor's Publish runs, so the block starts life as a live v1 with the kind's default content
 * (a Draft-only block cannot be duplicated or used in a template).
 *
 * A failure creating the block rejects. A failure publishing it does not: the block exists, so it
 * resolves with `published: false` and a `reason`, and the caller opens the editor on the Draft rather
 * than leaving the author in a dialog with nothing to retry. A prose kind (blank body by default) is
 * not published at all: it resolves `no-content` straight away.
 */
export function useCreateAndPublishBlock(documentTypeKey?: string | null) {
  const createBlock = useCreateBlock();
  const publishBlock = usePublishBlockVersion();

  const createAndPublish = async (payload: CreateBlockPayload): Promise<CreatedBlock> => {
    const created = await createBlock.mutateAsync(payload);
    const blockId = String(created.blockId);
    // The first version is what the editor shows (a letterhead's promoted sections and columns), so
    // the template preview and the PDF lay the block out the same way (LLAMS-686).
    const content = materializeEditorSections(defaultContentForKind(payload.kind, documentTypeKey));
    // A prose kind starts with an empty body, which the API refuses to publish (400): skip the doomed
    // request and open the editor on the Draft with the "add content, then publish" note (LLAMS-686).
    if (isBlankProseContent(content)) {
      return { blockId, published: false, reason: 'no-content' };
    }
    try {
      await publishBlock.mutateAsync({
        blockId,
        kind: payload.kind,
        content,
        style: DEFAULT_BLOCK_STYLE,
        // The create left a Draft v1 open, and a second POST while one is open is a 409: write into it.
        draftVersionId: openDraftIdOf(created.draftVersionId == null ? null : String(created.draftVersionId))
      });
      return { blockId, published: true };
    } catch (error: unknown) {
      defaultLogger.warn('The new block was created but its first version could not be published.', { blockId, error });
      const reason: UnpublishedReason = error instanceof HttpError && error.status === 400 ? 'no-content' : 'failed';
      return { blockId, published: false, reason };
    }
  };

  return { createAndPublish, isPending: createBlock.isPending || publishBlock.isPending };
}
